import axios from 'axios';
import _isEmpty from 'lodash.isempty';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    handleCloseDropdownOnBlur,
    handleCloseInputDropdownOnBlur,
    handleFusedeckSubmitDatalayer,
    handleLoadSharpsringScriptUrl,
    handleSetSharpspringScript,
    handleSharpspringSubmit,
    normalizeModelName,
    setCaptchaEuScript,
} from '../utils';

Vue.component('configurator-form', {
    props: {
        locale: {
            type: String,
        },
        siteHandle: {
            type: String,
        },
        stores: {
            value: Array,
        },
        model: {
            type: String,
        },
        sharpspringBaseUrl: {
            value: String,
        },
        sharpspringEndpointId: {
            value: String,
        },
        sharpspringScriptUrl: {
            value: String,
        },
        captchaEuPublicKey: {
            value: String,
        },
        cdnUrl: {
            type: String,
            required: false,
        },
        isCaptchaEuEnabled: {
            value: Boolean,
        },
    },

    data() {
        return {
            errors: new Errors(),
            submitting: false,
            dropdownCountry: null,
            countryHandle: '',

            configuration: {},
            configurationType: '',
            requestType: 'private',
            color: null,
            edition: null,
            version: null,
            editionOptions: [],
            paymentOption: null,
            promotions: [],
            totalPrice: '',
            formattedTotalPrice: '',
            price: '',
            formattedPrice: '',
            priceNa: false,

            dropdownHeardFrom: null,
            heardFrom: null,

            internalCountryHandle: '',
            dropdownFrStore: '',
            dropdownFrStoreValue: '',
            frBrandStores: {
                martinique: 'MG Martinique (97232 Le Lamentin)',
                guadeloupe: 'MG Guadeloupe (97122 Baie-Mahault)',
                guyane: 'MG Guyane (97324 Cayenne Cedex)',
                mayotte: 'MG Mayotte (97 600 Mamoudzou)',
            },

            guyane: 'GF',
            martinique: 'GF',
            mayotte: 'YT',
            reunion: 'RE',
            guadeloupe: 'GP',

            isNumberValid: true,
            isInterestedInChargingSolution: false,

            commonInputCountries: ['default', 'mgMotorsFrance'],

            agricoleDodDescription: '',
            isAgricoleCalculation: false,

            isSwitchButtonVisible: true, // used only for germany configurator (corporate switch)
            requestIndex: 0, // used only for germany configurator (corporate switch)

            newsletterSignUp: false,
        };
    },

    computed: {
        configurationJson() {
            return JSON.stringify(this.configuration);
        },
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },

        colorPickerClass() {
            return `color-${this.color.colorSlug}`;
        },

        colorPickerStyle() {
            return `background-color: ${this.color.colorCode}`;
        },

        isConfigurationEmpty() {
            return _isEmpty(this.configuration);
        },

        isItalyPriceClass() {
            return this.siteHandle === 'mgMotorsItaly'
                ? 'configurator__selected-price--italy'
                : '';
        },

        isSubmitLoading() {
            return this.submitting ? 'button--loading' : '';
        },

        houseAdditionInputClass() {
            const countries = ['mgMotorsNorway', 'mgMotorsFrance'];
            return countries.includes(this.siteHandle)
                ? 'input-container--addition-hidden'
                : '';
        },

        isStreetNameInputVisible() {
            const countries = ['default', 'mgMotorsFrance'];
            return countries.includes(this.siteHandle);
        },

        isHouseNumberInputVisible() {
            return this.commonInputCountries.includes(this.siteHandle);
        },

        isHouseNumberAdditionInputVisible() {
            const countries = ['default'];
            return countries.includes(this.siteHandle);
        },

        isPostalCodeInputVisible() {
            const countriesExcluded = [
                'mgMotorsIceland',
                'mgMotorsNorway',
                'mgMotorsSpain',
                'mgMotorsItaly',
                'mgMotorsPortugal',
                'mgMotorsSweden',
                'mgMotorsNetherlands',
                'mgMotorsHungary',
            ];

            return !countriesExcluded.includes(this.siteHandle);
        },

        isCityInputVisible() {
            return this.commonInputCountries.includes(this.siteHandle);
        },

        isCompanyInputVisible: function () {
            return (
                this.requestType === 'business' &&
                (this.siteHandle === 'mgMotorsGermany' ||
                    this.siteHandle === 'mgMotorsFrance' ||
                    this.siteHandle === 'mgMotorsSpain')
            );
        },
    },

    created() {
        // Listen to configuration completed event to update configuration result.
        EventBus.$on('configurator-completed', (configuration) => {
            this.configuration = configuration;

            this.configurationType = configuration.type.key;
            this.handleSetCompleteConfiguration(configuration);
        });

        // Listen to configuration completed event to update configuration result.
        EventBus.$on('initial-configurator-created', (configuration) => {
            this.configuration = configuration;

            this.configurationType = configuration.type.key;
        });

        // Update request type setting on event.
        EventBus.$on('configurator-set-request-type', (requestType) => {
            this.requestType = requestType;
        });

        // Listen for color change.
        EventBus.$on('configurator-set-color', (color) => {
            this.color = color;
        });

        // Listen for edition change.
        EventBus.$on('configurator-set-edition', (edition) => {
            this.edition = edition;
        });

        // Update version on version selection change.
        EventBus.$on('configurator-set-version', (version) => {
            this.version = version;
        });

        // Add listener to update edition options when they change.
        EventBus.$on('configurator-set-edition-options', (options) => {
            // Loop options to fill editionOptions with the ones that have been selected.
            this.editionOptions = [];
            for (let option in options) {
                if (options[option].selected) {
                    this.editionOptions.push(options[option]);
                }
            }
        });

        // Listen for edition change.
        EventBus.$on('configurator-set-payment-option', (paymentOption) => {
            this.paymentOption = paymentOption;
        });

        // Add listener to update promotions when they change.
        EventBus.$on('configurator-set-promotions', (promotions) => {
            // Loop promotions to fill editionOptions with the ones that have been selected.
            this.promotions = [];
            for (let promotion in promotions) {
                if (
                    promotions[promotion].selected &&
                    promotions[promotion].priceAddition < 0
                ) {
                    this.promotions.push(promotions[promotion]);
                }
            }
        });

        // Listen for prica n/a.
        EventBus.$on('configurator-price-na', () => {
            this.priceNa = true;
            this.totalPrice = '';
            this.formattedTotalPrice = '';
            this.price = '';
            this.formattedPrice = '';
        });

        // Listen for total-price change.
        EventBus.$on(
            'configurator-total-price-update',
            (totalPrice, formattedTotalPrice) => {
                this.priceNa = false;
                this.totalPrice = totalPrice;
                this.formattedTotalPrice = formattedTotalPrice;
                if (this.paymentOption.field_paymentOption_priceTextFormBlock) {
                    this.formattedTotalPrice =
                        this.paymentOption.field_paymentOption_priceTextFormBlock.replace(
                            '{price}',
                            totalPrice,
                        );
                }
            },
        );

        // Listen for total-price change.
        EventBus.$on('configurator-price-update', (price, formattedPrice) => {
            this.price = this.formatAsPrice(price.replace(' ', ''));
            this.formattedPrice = formattedPrice;
            if (this.paymentOption.field_paymentOption_priceTextFormBlock) {
                this.formattedPrice =
                    this.paymentOption.field_paymentOption_priceTextFormBlock.replace(
                        '{price}',
                        price,
                    );
            }
        });

        EventBus.$on(
            'agricole-calculator-leasing-finished',
            (monthlyInstallment) => {
                if (this.$parent.configuratorResult.price.key !== 'lease') {
                    return;
                }
                this.price = monthlyInstallment
                    ? this.formatAsPrice(
                          `${monthlyInstallment}`.replace(' ', ''),
                      )
                    : '';
            },
        );
    },

    mounted() {
        if (this.siteHandle !== 'default') {
            this.countryHandle = this.siteHandle;
        }

        handleSetSharpspringScript(
            this.siteHandle,
            this.sharpspringBaseUrl,
            this.sharpspringEndpointId,
            'configuratorForm',
        );
        handleLoadSharpsringScriptUrl(
            this.siteHandle,
            this.sharpspringScriptUrl,
        );
        setCaptchaEuScript();
        handleCloseDropdownOnBlur('#heard-from-dropdown');
        handleCloseDropdownOnBlur('#country-dropdown');
        handleCloseDropdownOnBlur('#brandstore-dropdown');
        handleCloseInputDropdownOnBlur('#store-dropdown-container', '#store');

        if (this.siteHandle === 'mgMotorsFrance') {
            EventBus.$on(
                'agricole-calculator-finished',
                ({ isAgricoleCalculation, dodDescription }) => {
                    this.isAgricoleCalculation = isAgricoleCalculation
                        ? '1'
                        : '0';
                    this.agricoleDodDescription = dodDescription;
                },
            );
        }
    },

    methods: {
        handleMarketingConsentChange(e) {
            e.preventDefault();

            this.newsletterSignUp = e.target.checked;
        },

        handleToggleButtonActiveClass(index) {
            return this.requestIndex === index ? 'checked' : '';
        },

        getCarImage(modelCommerceHandle) {
            return `${this.cdnUrl}/images/configurator/${modelCommerceHandle}/${this.configurationType}/model-preview/${this.configuration.color.key}.png`;
        },

        getModelName(modelName) {
            const slicedModelName = modelName.slice(0, 2).toLowerCase();
            return slicedModelName === 'mg' ? modelName : `MG ${modelName}`;
        },
        /**
         * Handle push tu data layer for GTM when configuration is completed.
         *
         * @returns {Void}
         */

        handleSetCompleteConfiguration(config) {
            if (checkCCM19Consent() || checkOneTrustConsent()) {
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                window.dataLayer.push({
                    event: 'complete_configuration',
                    car: {
                        name: normalizeModelName(this.model),
                        ids: [config.type.id],
                        edition: config.type.name,
                        color: config.color.name,
                    },
                });
            }
        },

        /**
         * Emit event to switch back to the configurator screen.
         *
         * @returns {Void}
         */
        returnToConfiguratorEdit(event) {
            event.preventDefault();
            EventBus.$emit('configurator-back-to-the-studio');
            if (this.siteHandle === 'mgMotorsFrance') {
                EventBus.$emit('agricole-calculator-leasing-finished', null);
                EventBus.$emit('toggle-agricole-calculator-footer-notes', true);
            }
        },

        /**
         * Use zenscroll to scroll to the first element that has a form error.
         *
         * @returns {Void}
         */
        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
                zenscroll.center(firstError);
            });
        },

        /**
         * Use zenscroll to scroll all the way up to the top.
         *
         * @returns {Void}
         */
        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        /**
         * Handles form submission.
         *
         * @param {SubmitEvent} event
         * @returns {Void}
         */
        async onSubmit(event) {
            event.preventDefault();
            const form = event.currentTarget;
            let dataFromForm = new FormData(form);
            let data = {};

            for (let [key, val] of dataFromForm.entries()) {
                Object.assign(data, { [key]: val });
            }

            if (
                this.siteHandle === 'mgMotorsFrance' &&
                this.agricoleDodDescription
            ) {
                data = {
                    ...data,
                    message: this.agricoleDodDescription,
                };
            }

            // Don't submit twice.
            if (this.submitting) {
                return;
            }
            this.submitting = true;

            const isCompany =
                data.hasOwnProperty('company') && Boolean(data.company.length);

            const dataLayerData = {
                event: 'lead',
                lead_type: 'Finance Quote',
                client_type: isCompany ? 'B2B' : 'B2C',
                payment_type: this.paymentOption,
                brandstore: data.store,
                car: {
                    name: normalizeModelName(data.model),
                    ids: [this.configuration.type.id],
                    edition: this.configuration.type.name,
                    color: this.configuration.color.name,
                },
                user_data: {
                    email_address: data.emailAddress,
                    phone_number: data.phone,
                    address: {
                        first_name: data.firstName,
                        last_name: data.lastName,
                    },
                },
            };

            if (!this.isNumberValid) {
                this.submitting = false;
                return;
            }

            try {
                if (!this.isCaptchaEuEnabled) {
                    await this.handleDataSubmit(dataLayerData);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(dataLayerData, captchaSolution);
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(dataLayerData, captchaSolution) {
            try {
                // Set form data.
                const isFormRefArray = Array.isArray(this.$refs.form);

                const formData = new FormData(
                    isFormRefArray ? this.$refs.form[0] : this.$refs.form,
                );
                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }

                // Post form to craft.
                const response = await axios.post('/', formData);
                // Fetch result.
                const result = response.data;

                // Handle form errors.
                if (result.data.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);
                    this.submitting = false;
                    this.scrollToFirstError();
                    return;
                }

                this.sendGTMData(formData, dataLayerData);
                handleSharpspringSubmit(
                    this.siteHandle,
                    this.sharpspringEndpointId,
                );

                const switzerlandCountries = [
                    'mgMotorsSwitzerlandIt',
                    'mgMotorsSwitzerlandDe',
                    'mgMotorsSwitzerlandFr',
                ];

                if (switzerlandCountries.includes(this.siteHandle)) {
                    await handleFusedeckSubmitDatalayer();
                }

                // Track form submission.
                this.trackFormSubmit(formData);
                this.isNumberValid = true;

                // Redirect to the thank you page.
                window.location = result.data.redirectUrl;
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData(formData, extraDataToPush) {
            const country =
                this.siteHandle == 'default'
                    ? 'mgMotorsEurope'
                    : this.siteHandle;

            const model = formData.get('model');

            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                if (extraDataToPush) {
                    window.dataLayer.push(extraDataToPush);
                }

                window.dataLayer.push({
                    event: 'configuration',
                    model: normalizeModelName(model),
                    edition: `${this.configuration.type.name} - ${this.configuration.version.name}`,
                    page_path: window.location.href,
                    country: `${country} - ${this.$root.languageCode}`,
                });
            }
        },

        /**
         * Trigger tracking of form submission.
         *
         * @returns {Void}
         */
        trackFormSubmit(formData) {
            // Only track in production environment.
            if (Vue.config.devtools) {
                return;
            }

            // TODO: Remove or uncomment later
            // // Track FB.
            // if (typeof fbq === 'function') {
            //     fbq('track', 'Lead', {
            //         lead_type: 'Lead',
            //         country: this.siteHandle,
            //         color: this.configuration.color.key,
            //         edition: this.configuration.type.key,
            //         payment_type: this.configuration.priceIndication,
            //     }); // NL
            //     fbq('track', 'CustomizeProduct', {
            //         content_type: formData.get('model'),
            //         content_ids: this.configuration.type.id,
            //         client_type: this.requestType == 'private' ? 'B2C' : 'B2B',
            //         brandstore_type: this.selectedBrandStore,
            //         color: this.configuration.color.key,
            //         country: this.siteHandle,
            //         edition: this.configuration.type.key,
            //         payment_type: this.configuration.price.key,
            //     });
            // }

            // Track floodlight.
            var axel = Math.random() + '';
            var a = axel * 10000000000000;
            let iframe = document.createElement('iframe');
            iframe.src =
                'https://10256359.fls.doubleclick.net/activityi;src=10256359;type=mg_co0;cat=mg_le02;u1=' +
                this.siteHandle +
                ';u2=[language];u3=' +
                window.location.pathname +
                ';u4=[page_name];u8=[business];u9=zs-ev;u10=' +
                this.configuration.color.key +
                ';u11=' +
                this.configuration.type.key +
                ';dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;npa=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ord=' +
                a +
                '?';
            iframe.width = 1;
            iframe.height = 1;
            iframe.frameborder = 0;
            iframe.style.display = 'none';
            document.body.appendChild(iframe);
        },

        /**
         * Open or close a custom dropdown element.
         *
         * @param {ClickEvent|ChangeEvent} event
         */
        toggleDropdown(event) {
            event.preventDefault();
            let element = event.currentTarget;
            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },

        /**
         * Update heardFrom value when a selection takes place in its custom dropdown element.
         *
         * @param {ChangeEvent} event
         */
        setDropdownHeardFrom(event) {
            // Set text property to show value in template, set value in heardFrom property to send along with the form.
            this.dropdownHeardFrom = event.currentTarget.dataset.text;
            this.heardFrom = event.currentTarget.dataset.value;

            // Close the dropdown.
            this.toggleDropdown(event);
        },

        /**
         * Formats a given float as a price, with no decimals if there are none, or 2 if there are.
         * @param {Float} price
         * @returns {String}
         */
        formatAsPrice(price) {
            // Set localization formatting.
            let countyPriceMapping = {
                localization: 'de-DE',
                showZeros: false,
            };
            if (this.$parent.countyPriceMapping[this.locale] != undefined) {
                countyPriceMapping =
                    this.$parent.countyPriceMapping[this.locale];
            }

            // Format with 2 decimals if price as any decimals.
            if (price % 1 != 0 || countyPriceMapping.showZeros) {
                return price.toLocaleString(countyPriceMapping.localization, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            }

            // Format with no decimal digits.
            return price.toLocaleString(countyPriceMapping.localization);
        },

        brandStoreSelected(store) {
            this.selectedBrandStore = store;
        },

        setDropdownCountry(event) {
            this.toggleDropdown(event);

            this.dropdownCountry = event.currentTarget.dataset.text;
            this.countryHandle = event.currentTarget.dataset.value;
            this.internalCountryHandle = event.currentTarget.dataset.dorm;

            const territory = event.currentTarget.dataset.dorm;
            if (
                ['guyane', 'guadeloupe', 'martinique', 'mayotte'].includes(
                    territory,
                )
            ) {
                this.internalCountryHandle = territory;
                this.frBrandStoreHandle = territory;
                this.dropdownFrStore = this.frBrandStores[territory];

                this.dropdownFrStoreValue = this.frBrandStores[territory];

                return;
            }

            if (territory === 'reunion') {
                this.internalCountryHandle = territory;
                this.frBrandStoreHandle = 'MG Saint-Pierre (97 410)';
                this.dropdownFrStore = 'MG Saint-Pierre (97 410)';
                this.dropdownFrStoreValue = 'MG Saint-Pierre (97 410)';
            }
        },

        setDropdownFrBrandStore(event) {
            const text = event.currentTarget.dataset.text;
            this.dropdownFrStore = text;
            this.dropdownFrStoreValue = text;

            this.frBrandStoreHandle = event.currentTarget.dataset.value;

            this.toggleDropdown(event);
        },

        toggleIsInterestedInChargingSolution() {
            this.isInterestedInChargingSolution =
                !this.isInterestedInChargingSolution;
        },
    },
});
